import * as React from 'react'
import { Position } from '@blueprintjs/core'
import camelCase from 'lodash.camelcase'
import { useEffect, useState } from 'react'
import { useEtsyChannelFormContext } from '../../../../products_v4/listings/etsy/EtsyChannelFormContext'
import { ChannelPriceInput, ChannelSelectInput, SharedQuantityInput } from '../ChannelShared/SharedInputs'
import { etsyFields, lookupComponent } from './EtsyFormHelpers'
import CHANNELS from '../../../../../constants/Channels'
import CategoryMarketCategoryPickerV2 from '../../../../../components/Categories/CategoryMarketCategoryPickerV2'
import { camelize } from '../../../../../utils'
import Client from '../../../../../client'
import { showFailure } from '../../../../../Toast'

export const ETSY_MAX_TITLE_LENGTH = 140

interface Props {
  sectionOpen: boolean
  skipErrorPrefix?: boolean
  priceRecommendation?: any
}

interface ProfileOptions {
  value: number
  label: string
}
export const EtsyForm = React.memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { skipErrorPrefix, sectionOpen, priceRecommendation } = props
  const { form, onListingChange, errors, productUnitTracking } = useEtsyChannelFormContext()
  const [shippingProfileOptions, setShippingProfileOptions] = useState<ProfileOptions[]>()
  const [returnPoliciesOptions, setReturnPoliciesOptions] = useState<ProfileOptions[]>()
  const [fetchError, setFetchError] = useState('')

  const handleCategoryChange = React.useCallback(
    (v: number) => {
      onListingChange({ name: 'categoryMarketCategoryId', value: v })
    },
    [onListingChange]
  )

  useEffect(() => {
    const fetchMarketSellerProfiles = async () => {
      const etsyMarketId = window.current.organization.connectedMarkets.find(
        market => market.humanReadableName === 'Etsy'
      ).marketAccountId

      try {
        const res = await Client.getMarketSellerProfilesV5(etsyMarketId, true)
        const { shippingProfiles, returnPolicies } = res.data

        setShippingProfileOptions(shippingProfiles.map(profile => ({ label: profile.name, value: profile.externalId })))
        setReturnPoliciesOptions(returnPolicies.map(profile => ({ label: profile.name, value: profile.externalId })))

        if (!form.shippingProfileId) {
          const defaultShipping = shippingProfiles.find(p => p.default)
          if (defaultShipping) {
            onListingChange({ name: 'shippingProfileId', value: defaultShipping.externalId })
          }
        }

        if (!form.returnPolicyId) {
          const defaultReturn = returnPolicies.find(p => p.default)
          if (defaultReturn) {
            onListingChange({ name: 'returnPolicyId', value: defaultReturn.externalId })
          }
        }
        setFetchError('')
      } catch (error) {
        setFetchError(error.response?.data?.error || 'Failed to fetch Etsy profiles')
        showFailure(error.response?.data?.error || 'Failed to fetch Etsy profiles')
      }
    }
    fetchMarketSellerProfiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // set default values before
  useEffect(() => {
    Object.entries(etsyFields).forEach(([key, field]) => {
      const formKey = camelCase(key)
      if (field.default && !form[formKey]) {
        onListingChange({ name: formKey, value: field.default })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="etsy-form channel-form">
      <div className="stretch-12">
        <CategoryMarketCategoryPickerV2
          value={form.categoryMarketCategoryId}
          error={skipErrorPrefix ? errors.categoryMarketCategoryId : errors.etsyListingCategoryMarketCategoryId}
          errorTooltipOffset={sectionOpen && 45}
          marketId={CHANNELS.ETSY.id}
          onChange={handleCategoryChange}
          simpleValue
        />
      </div>
      <p style={{ color: 'red' }}>{fetchError}</p>
      <div className="stretch-12">
        <ChannelSelectInput
          label="Shipping Profile"
          name="shippingProfileId"
          value={form.shippingProfileId}
          onListingChange={onListingChange}
          options={shippingProfileOptions || []}
          error={skipErrorPrefix ? errors.shippingProfileId : errors.etsyListingShippingProfileId}
          errorTooltipOffset={sectionOpen && 45}
          errorTooltipPosition={Position.LEFT}
        />
      </div>
      <div className="stretch-12">
        <ChannelSelectInput
          label="Return Policy"
          name="returnPolicyId"
          value={form.returnPolicyId}
          onListingChange={onListingChange}
          options={returnPoliciesOptions || []}
          error={skipErrorPrefix ? errors.returnPolicyId : errors.etsyListingReturnPolicyId}
          errorTooltipOffset={sectionOpen && 45}
          errorTooltipPosition={Position.LEFT}
        />
      </div>
      <div className="span-2">
        <ChannelPriceInput
          error={skipErrorPrefix ? errors.price : errors.etsyListingPrice}
          showRequiredLabel
          value={form.price}
          onListingChange={onListingChange}
          errorTooltipOffset={sectionOpen && 45}
        />
      </div>
      <div className="span-2">
        <SharedQuantityInput
          error={skipErrorPrefix ? errors.quantity : errors.etsyListingQuantity}
          minorStepSize={1}
          min={1}
          value={form.quantity}
          disabled={productUnitTracking}
          helperText={productUnitTracking ? 'Unit-tracking products automatically manage quantity' : ''}
          onListingChange={onListingChange}
          errorTooltipOffset={sectionOpen && 232}
          errorTooltipPosition={Position.LEFT}
        />
      </div>
      {/* Etsy specific fields */}
      {Object.entries(etsyFields).map(([key, field]) => {
        const Component = lookupComponent[field.type]
        const camelizedErrorKey = skipErrorPrefix ? field.errorKey : camelize(`etsy_listing_${field.errorKey}`)
        const value = form[camelCase(key)]
        return (
          <div key={key} className="span-4">
            <Component
              field={field}
              value={value}
              onListingChange={v => onListingChange({ name: camelCase(key), value: v })}
              error={errors[camelizedErrorKey]}
            />
          </div>
        )
      })}
    </div>
  )
})
